import React, { useCallback } from 'react';
import { View, StyleSheet, ScrollView, RefreshControl } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useCombinedConversations, UnifiedConversation } from '@/hooks/residents/useCombinedConversations';
import type { MessagesStackParamList } from '@/navigation/residents/mobile/MessagesStackNavigator';
import type { StackNavigationProp } from '@react-navigation/stack';
import { ConversationListSkeleton } from './ConversationListSkeleton';
import { REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { useThemeColors, useThemedStyles } from '@/hooks/residents/useThemeColors';


import { PageHeader } from '@/components/residents/redesign';
import { ScreenBackLink } from '@/components/residents/mobile/ScreenBackLink';
import {
  ChatGlassListHeader,
  ChatGlassConversationCard,
  ChatGlassEmptyState,
  SortOption,
} from '@/components/residents/redesign/chat';

type MessagesNav = StackNavigationProp<MessagesStackParamList, 'ConversationList'>;

export const ConversationListScreen: React.FC = () => {
  const { colors, mode } = useThemeColors();
  const styles = useConversationListStyles();
  const navigation = useNavigation<MessagesNav>();
  const insets = useSafeAreaInsets();

  const {
    conversations,
    isLoading,
    refreshing,
    isEmpty,
    searchQuery,
    setSearchQuery,
    sortOption,
    setSortOption,
    refetchAll,
    markAsReadLocally,
  } = useCombinedConversations();

  const handleRefresh = useCallback(async () => {
    await refetchAll();
  }, [refetchAll]);

  const handleSortChange = useCallback((option: SortOption) => {
    setSortOption(option as any);
  }, [setSortOption]);

  const handleComposePress = useCallback(() => {
    navigation.navigate('NewConversation');
  }, [navigation]);

  const handleConversationPress = useCallback((id: number | string) => {
    const conversation = conversations.find((c: UnifiedConversation) => c.id === id);
    if (!conversation) return;

    if (conversation.hasUnread) {
      markAsReadLocally(conversation.originalId);
    }

    navigation.navigate('Conversation', {
      conversationId: conversation.originalId,
      subject: conversation.title,
    });
  }, [conversations, navigation, markAsReadLocally]);

  const renderContent = () => {
    if (isLoading && !refreshing) {
      return <ConversationListSkeleton />;
    }

    if (isEmpty) {
      return (
        <View style={styles.glassContainer}>
          <ChatGlassListHeader
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            onComposePress={handleComposePress}
            sortOption={sortOption as SortOption}
            onSortChange={handleSortChange}
            hideSortRow
          />
          <View style={styles.emptyStateWrap}>
            <ChatGlassEmptyState type="noMessages" />
          </View>
        </View>
      );
    }

    return (
      <View style={styles.glassContainer}>
        <ChatGlassListHeader
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onComposePress={handleComposePress}
          sortOption={sortOption as SortOption}
          onSortChange={handleSortChange}
        />
        <View>
          {conversations.map((item: UnifiedConversation) => (
            <ChatGlassConversationCard
              key={item.id}
              id={item.id}
              subject={item.title}
              preview={item.preview}
              date={item.date}
              isGroup={item.isGroup}
              recipientCount={item.recipientCount}
              hasUnread={item.hasUnread}
              onPress={handleConversationPress}
            />
          ))}
        </View>
      </View>
    );
  };

  return (
    <ScrollView
      style={styles.scrollView}
      contentContainerStyle={[
        styles.scrollContent,
        { paddingBottom: 40 + insets.bottom },
      ]}
      showsVerticalScrollIndicator={false}
      refreshControl={
        <RefreshControl
          refreshing={refreshing}
          onRefresh={handleRefresh}
          tintColor={colors.text.primary}
        />
      }
    >
      <ScreenBackLink />
      <PageHeader title="Property Chat" subtitle="Messages with your property manager" />
      {renderContent()}
    </ScrollView>
  );
};

const useConversationListStyles = () =>
  useThemedStyles(({ colors }) =>
    StyleSheet.create({
      scrollView: { flex: 1 },
      scrollContent: {
        paddingHorizontal: REDESIGN_SPACING.horizontalPadding,
        gap: 0,
      },
      glassContainer: {
        marginTop: 16,
        backgroundColor: colors.card.bg,
        borderRadius: 20,
        borderWidth: 1,
        borderColor: colors.card.border,
        overflow: 'hidden',
      },
      emptyStateWrap: {
        paddingBottom: 48,
      },
    }),
  );

export default ConversationListScreen;
